import { useState, useEffect, useRef } from 'react';
import Button from '../../../components/base/Button';

declare global {
  interface Window {
    Calendly: any;
  }
}

export default function ServicesSection() {
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const calendlyRef = useRef<HTMLDivElement>(null);

  const mainService = {
    title: 'Individual Session (Online & In-Person)',
    duration: '1 hour of personalized art therapy',
    price: '2500 THB',
    calendlyUrl: 'https://calendly.com/yuvati-art/1-hour-online-session'
  };

  // Load Calendly script once
  useEffect(() => {
    if (window.Calendly) {
      setCalendlyLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      setCalendlyLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Calendly script');
      setCalendlyLoaded(false);
    };
    
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);

  // Load Stripe script
  useEffect(() => {
    const stripeScript = document.createElement('script');
    stripeScript.src = 'https://js.stripe.com/v3/buy-button.js';
    stripeScript.async = true;
    document.head.appendChild(stripeScript);

    return () => {
      const existingStripeScript = document.querySelector('script[src="https://js.stripe.com/v3/buy-button.js"]');
      if (existingStripeScript && existingStripeScript.parentNode) {
        existingStripeScript.parentNode.removeChild(existingStripeScript);
      }
    };
  }, []);

  // Initialize Calendly widget
  useEffect(() => {
    if (!calendlyLoaded || !window.Calendly || !calendlyRef.current) return;

    setWidgetLoading(true);
    
    // Clear existing widget
    calendlyRef.current.innerHTML = '';
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (calendlyRef.current && window.Calendly) {
        try {
          window.Calendly.initInlineWidget({
            url: mainService.calendlyUrl,
            parentElement: calendlyRef.current,
            prefill: {},
            utm: {},
            styles: {
              height: '900px'
            }
          });
          setWidgetLoading(false);
        } catch (error) {
          console.error('Error initializing Calendly widget:', error);
          setWidgetLoading(false);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [calendlyLoaded]);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openLegalModal = () => {
    setShowLegalModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLegalModal = () => {
    setShowLegalModal(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      {/* Legal Information Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#385B47]/50 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col my-4">
            {/* Modal Header */}
            <div className="px-4 sm:px-8 py-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 
                  className="text-xl sm:text-2xl font-normal cinematic-text-dark pr-4"
                >
                  Legal Information & Cancellation Policy
                </h3>
                <button
                  onClick={closeLegalModal}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                  aria-label="Close modal"
                >
                  <i className="ri-close-line text-2xl text-[#312921]/70"></i>
                </button>
              </div>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="px-4 sm:px-8 py-6 overflow-y-auto flex-grow">
              <div className="space-y-6 text-[#312921]/85">
                {/* Disclaimer */}
                <div>
                  <h4 className="text-base sm:text-lg font-normal text-[#312921] mb-3">Disclaimer</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    Art therapy is a space for self-exploration, creativity, and emotional support.
                    It can be a meaningful part of your healing journey, but it is not a substitute for medical, psychiatric, or psychological treatment if such care is needed.
                    By booking a session, you acknowledge responsibility for your own emotional well-being and personal decisions made during or after the session.
                  </p>
                </div>

                {/* Liability */}
                <div>
                  <h4 className="text-base sm:text-lg font-normal text-[#312921] mb-3">Liability</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    The therapist is not responsible for any outcomes, emotional reactions, or decisions made based on the content of the session.
                    Sessions are designed to provide gentle guidance and self-reflection, not to diagnose or offer medical advice.
                  </p>
                </div>

                {/* Confidentiality */}
                <div>
                  <h4 className="text-base sm:text-lg font-normal text-[#312921] mb-3">Confidentiality</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    Everything you share during sessions is treated with care and confidentiality.
                    Your information will never be shared with third parties unless required by law or if there is a serious risk of harm to yourself or others.
                  </p>
                </div>

                {/* Cancellations & Rescheduling */}
                <div>
                  <h4 className="text-base sm:text-lg font-normal text-[#312921] mb-3">Cancellations & Rescheduling</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    I completely understand that life can be unpredictable.
                    If you need to reschedule or cancel, please let me know at least 24 hours in advance.
                    This allows me to offer the time to someone else who may be waiting.
                    Cancellations made less than 24 hours before the session or missed appointments are non-refundable.
                  </p>
                </div>

                {/* Late Arrival */}
                <div>
                  <h4 className="text-base sm:text-lg font-normal text-[#312921] mb-3">Late Arrival</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    If you arrive late, the session may still need to end at the originally scheduled time so that other clients can begin on time.
                  </p>
                </div>

                {/* Technical Issues */}
                <div>
                  <h4 className="text-base sm:text-lg font-normal text-[#312921] mb-3">Technical Issues (for online sessions)</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    If there are any technical difficulties, please don't worry.
                    If the issue is on my side, the session will be rescheduled free of charge.
                  </p>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-base sm:text-lg font-normal text-[#312921] mb-3">Privacy Policy — Yuvati Art Company Limited</h4>
                  <p className="leading-relaxed text-sm sm:text-base mb-4">
                    This Privacy Policy explains how Yuvati Art Company Limited (Reg. No. 0505567017184) collects, uses, and protects your personal data in accordance with Thailand's Personal Data Protection Act B.E. 2562 (2019).
                  </p>

                  <h5 className="text-sm sm:text-base font-normal text-[#312921] mb-2">What Personal Data We Collect</h5>
                  <p className="leading-relaxed text-sm sm:text-base mb-4">
                    We may collect your name, email address, phone or messaging handle, booking details, and payment information. Payments are processed directly by Stripe; we do not store your full card details.
                  </p>

                  <h5 className="text-sm sm:text-base font-normal text-[#312921] mb-2">How We Use Your Data</h5>
                  <p className="leading-relaxed text-sm sm:text-base mb-4">
                    We use your data to schedule and manage sessions, process payments, and communicate with you. We do not use your data for automated decision-making or unsolicited marketing.
                  </p>

                  <h5 className="text-sm sm:text-base font-normal text-[#312921] mb-2">Who We Share Your Data With</h5>
                  <p className="leading-relaxed text-sm sm:text-base mb-4">
                    We share limited data with Calendly (booking), Stripe (payments), Google Workspace (email and calendar), and WhatsApp/Telegram (communication), solely to operate these services. These providers may process data outside Thailand. We do not sell or rent your data to third parties.
                  </p>

                  <h5 className="text-sm sm:text-base font-normal text-[#312921] mb-2">Data Retention</h5>
                  <p className="leading-relaxed text-sm sm:text-base mb-4">
                    We retain your data only as long as necessary for the purposes described above, including any period required for tax and accounting records under Thai law.
                  </p>

                  <h5 className="text-sm sm:text-base font-normal text-[#312921] mb-2">Your Rights</h5>
                  <p className="leading-relaxed text-sm sm:text-base">
                    Under the PDPA, you have the right to access, correct, or request deletion of your personal data, withdraw consent at any time, and lodge a complaint with Thailand's Personal Data Protection Committee (PDPC). To exercise these rights, contact us at <a href="mailto:yuliya@yuvati.art" className="text-[#D6794E] hover:text-[#D6794E] underline">yuliya@yuvati.art</a>.
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-4 sm:px-8 py-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeLegalModal}
                className="w-full px-6 py-3 rounded-xl bg-[#2F4C3B] text-white font-normal hover:bg-[#385B47] transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Services & Pricing Header */}
      <section id="services-pricing" className="pt-8 pb-8 cinematic-bg-light-alt overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-4">
            <h2 
              className="text-4xl lg:text-5xl font-normal cinematic-text-dark mb-4 px-2"
            >
              Services & Pricing
            </h2>
            
            {/* Step-by-Step Instruction - Moved here */}
            <div className="flex items-center justify-center gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#D6794E] text-white rounded-full flex items-center justify-center font-normal text-sm flex-shrink-0">
                  1
                </div>
                <span className="text-sm sm:text-base font-normal text-[#312921]/85">Book your session</span>
              </div>
              <i className="ri-arrow-right-line text-[#312921]/40 text-lg"></i>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#D6794E] text-white rounded-full flex items-center justify-center font-normal text-sm flex-shrink-0">
                  2
                </div>
                <span className="text-sm sm:text-base font-normal text-[#312921]/85">Pay</span>
              </div>
            </div>
          </div>
          
          {/* Two Column Layout - Desktop / Stacked - Mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-start lg:items-stretch w-full">
            
            {/* About the Session & Next Steps - first on mobile, right column top on desktop */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg border border-gray-100 lg:col-start-2 lg:row-start-1">
              {/* About the Session */}
              <div className="mb-8">
                <div className="mb-6">
                  <h3 className="text-2xl sm:text-3xl font-normal cinematic-text-dark mb-2 font-playfair">
                    About the Session
                  </h3>
                  <p className="text-base sm:text-lg text-[#D6794E] font-normal mb-4">
                    A personalized path to healing through art
                  </p>
                </div>
                
                <p className="text-sm sm:text-base text-[#312921]/85 mb-4 leading-relaxed">
                  Experience the power of art therapy in a safe, supportive space — online or in person. Each session is tailored to your unique needs, providing a safe environment for self-expression and healing through creative exploration.
                </p>
                
                <ul className="space-y-2 text-sm sm:text-base text-[#312921]/85 leading-relaxed">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>No special materials are required.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>For online sessions, a simple pen and a sheet of paper are enough.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>For in-person sessions at 360 Art Center, all materials are provided.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>If you wish, you may prepare any art materials that feel interesting or emotionally engaging — they can enrich the creative process, but they are not required.</span>
                  </li>
                </ul>
              </div>

              {/* Your Next Step */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-normal cinematic-text-dark mb-4 font-playfair">
                  Your Next Step
                </h3>
                
                <ul className="space-y-2 text-sm sm:text-base text-[#312921]/85 leading-relaxed">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Select a date and time from the calendar</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Complete the secure payment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>You'll receive an email confirmation with all details</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Calendly Widget - second on mobile, left column on desktop */}
            <div id="calendly-widget" className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg border border-gray-100 flex flex-col w-full min-w-0 lg:col-start-1 lg:row-start-1 lg:row-span-2 h-full">
              
              {/* Session Description Block - Moved from right column */}
              <div className="mb-6 pb-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-2xl sm:text-3xl font-normal cinematic-text-dark mb-3 font-playfair break-words">
                  Schedule Your Individual Session (Online or In-person)
                </h3>
                <p className="text-sm sm:text-base text-[#312921]/85 leading-relaxed mb-4 break-words">
                  50-minute art therapy session — available online or in-person in Chiang Mai — designed to support self-expression, emotional clarity, and gentle creative healing.
                </p>
                <p className="text-xs sm:text-sm text-[#312921]/85 break-words">
                  By booking a session, you agree to the{' '}
                  <button
                    onClick={openLegalModal}
                    className="text-[#D6794E] hover:text-[#D6794E] underline cursor-pointer font-normal"
                  >
                    Legal Information & Cancellation Policy
                  </button>
                  .
                </p>
              </div>
              
              <div className="relative w-full min-w-0 flex-shrink-0">
                <div 
                  ref={calendlyRef}
                  className="w-full transition-opacity duration-500" 
                  style={{ 
                    height: '900px',
                    opacity: (!calendlyLoaded || widgetLoading) ? 0 : 1
                  }}
                ></div>
                
                {(!calendlyLoaded || widgetLoading) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg" style={{ height: '900px' }}>
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D6794E] mx-auto mb-4"></div>
                      <p className="text-sm text-[#312921]/85">
                        Loading calendar...
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              <p className="text-xs text-[#312921]/85 mt-4 text-center px-2 flex-shrink-0 break-words">
                Select your preferred date and time from the calendar above
              </p>
            </div>
            
            {/* Payment Cards & Help - third on mobile, right column bottom on desktop */}
            <div className="flex flex-col gap-6 w-full min-w-0 lg:col-start-2 lg:row-start-2">
              
              {/* 2. Yellow Background Block - Individual session */}
              <div className="bg-white border-[0.5px] border-[#DBCEC5] rounded-3xl p-6 sm:p-8 shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-100 rounded-full opacity-20 transform translate-x-16 -translate-y-16"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D6794E] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-star-fill text-white text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-normal text-[#385B47] font-playfair">Individual session</h3>
                      </div>
                    </div>
                    <div className="text-2xl sm:text-3xl font-normal text-[#D6794E] font-playfair">2500 THB</div>
                  </div>

                  <a
                    href="https://buy.stripe.com/eVq3cv1kDfBJgAS6a1d3i06"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <button className="w-full px-8 py-3 bg-[#D6794E] hover:bg-[#385B47] text-white font-normal rounded-xl shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base">
                      Pay
                    </button>
                  </a>
                </div>
              </div>

              {/* 3. Cocoa Brown Background Block - Therapeutic process */}
              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-100 rounded-full opacity-20 transform translate-x-16 -translate-y-16"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D6794E] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-check-fill text-white text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-normal text-[#385B47] font-playfair">Therapeutic process<br />(10 sessions)</h3>
                      </div>
                    </div>
                    <div className="text-2xl sm:text-3xl font-normal text-[#D6794E] font-playfair">22,000 THB</div>
                  </div>

                  <p className="text-sm text-[#312921] mb-4 leading-relaxed">
                    Most therapeutic work unfolds over time. The multi-session format supports depth, continuity, and integration.
                  </p>

                  <a
                    href="https://buy.stripe.com/3cI28r2oH0GP4Sacypd3i0g"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <button className="w-full px-8 py-3 bg-[#D6794E] hover:bg-[#385B47] text-white font-normal rounded-xl shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base">
                      Pay
                    </button>
                  </a>
                </div>
              </div>

              {/* Need Help Block */}
              <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg border border-gray-100 lg:flex-1">
                <h3 className="text-2xl sm:text-3xl font-normal cinematic-text-dark mb-4 font-playfair">
                  Need Help?
                </h3>
                
                <p className="text-sm sm:text-base text-[#312921]/85 leading-relaxed mb-6">
                  If you have any questions, feel free to contact me in any convenient way.
                </p>

                <button
                  onClick={scrollToContact}
                  className="w-full px-8 py-3 bg-[#D6794E] hover:bg-[#385B47] text-white font-normal rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  Contact me
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
